import { memo } from "react";

import { Listbox, Transition } from "@headlessui/react";

import { Icon, type IconName } from "@react/components/Icon";
import { Text } from "@react/components/Text";
import { tw, type TTailwindString } from "classnames";

import { type DropdownOption } from "./Dropdown.data";

type Props = {
  align?: "left" | "right";
  ariaLabel?: string;
  errorMessage?: string;
  fluid?: boolean;
  hasError?: boolean;
  helperText?: string;
  iconName?: IconName;
  isBorderless?: boolean;
  isDisabled?: boolean;
  label?: string;
  labelPosition?: "left" | "top";
  onSelectOption: (option: DropdownOption) => void;
  options: DropdownOption[];
  selectedOption: DropdownOption;
};

const styles = {
  base: ({ fluid }: { fluid: boolean }) =>
    tw("gap-x-2", "flex-col", {
      flex: fluid,
      "inline-flex": !fluid,
    }),
  selectedOptionButton: ({
    fluid,
    isDisabled,
    labelPosition,
  }: {
    fluid: boolean;
    isDisabled: boolean;
    labelPosition: "left" | "top";
  }) =>
    tw("p-0", "border-0", "bg-transparent", "text-left", {
      "w-full": fluid,
      "cursor-pointer": !isDisabled,
      [tw("flex", "items-center")]: labelPosition === "left",
    }),
  selectedOptionContainer: ({
    fluid,
    hasError,
    isBorderless,
    isDisabled,
  }: {
    fluid: boolean;
    hasError: boolean;
    isBorderless: boolean;
    isDisabled: boolean;
  }) =>
    tw("flex", "relative", "items-center", "box-border", {
      [tw(
        "border",
        "rounded",
        "border-solid",
        "px-4",
        "py-2.5",
        "text-primary"
      )]: !isBorderless,
      [tw("w-full", "justify-between")]: fluid,
      [tw("border-primary", "bg-primary-inverse")]:
        !isBorderless && !hasError && !isDisabled,
      [tw("border-interactive-error", "bg-interactive-error-textarea")]:
        !isBorderless && hasError,
      [tw(
        "border-interactive-disabled-pressed",
        "bg-interactive-disabled-background",
        "text-interactive-disabled-text"
      )]: !isBorderless && isDisabled,
    }),
  selectedOptionGroup: tw("flex", "flex-1"),
  selectedOption: ({
    isBorderless,
    isDisabled,
  }: {
    isBorderless: boolean;
    isDisabled: boolean;
  }) =>
    tw({
      "text-primary-muted": isBorderless,
      "text-interactive-disabled-pressed": isDisabled,
    }),
  arrow: ({ isDisabled, open }: { isDisabled: boolean; open: boolean }) =>
    tw(
      "h-[23px]" as TTailwindString,
      "w-[23px]" as TTailwindString,
      "md:h-[26px]" as TTailwindString,
      "md:w-[26px]" as TTailwindString,
      "ml-1",
      "transition-transform",
      "duration-300",
      {
        "rotate-180": open,
        "text-primary": !isDisabled,
        "text-interactive-disabled-pressed": isDisabled,
      }
    ),
  checkmark: tw("text-primary", "h-5", "w-5", "md:h-6", "md:w-6"),
  label: ({
    isDisabled,
    labelPosition,
  }: {
    isDisabled: boolean;
    labelPosition: "top" | "left";
  }) =>
    tw("shrink-0", "block", {
      "mb-1": labelPosition === "top",
      "mr-2": labelPosition === "left",
      [tw("text-primary", "cursor-pointer")]: !isDisabled,
      "text-interactive-disabled-text": isDisabled,
    }),
  optionsContainer: (align: "left" | "right", fluid: boolean) =>
    tw(
      "absolute",
      "z-10",
      "top-full",
      "bg-white",
      {
        "w-full": fluid,
        "w-64": !fluid,
      },
      "rounded",
      "border-solid",
      "border-primary",
      "mt-2",
      "shadow-[10px_10px_50px_10px_rgba(0,0,0,0.1)]" as TTailwindString,
      {
        "-left-px": align === "left",
        "right-0": align === "right",
      }
    ),
  options: tw("m-0", "p-0", "list-none"),
  optionsTransition: {
    enter: tw("transition", "duration-300", "ease-out"),
    enterFrom: tw("transform", "scale-95", "opacity-0"),
    enterTo: tw("transform", "scale-100", "opacity-100"),
    leave: tw("transition", "duration-150", "ease-out"),
    leaveFrom: tw("transform", "scale-100", "opacity-100"),
    leaveTo: tw("transform", "scale-95", "opacity-0"),
  },
  option: (active: boolean) =>
    tw(
      "px-4",
      "py-[13.5px]" as TTailwindString,
      "hover:bg-button-secondary-background-hover",
      "cursor-pointer",
      "flex",
      "justify-between",
      "items-center",
      "text-primary",
      {
        "bg-button-secondary-background-hover": active,
      }
    ),
  helperTextContainer: tw("flex", "items-center", "mt-2"),
  helperText: ({
    hasError,
    isDisabled,
  }: {
    hasError: boolean;
    isDisabled: boolean;
  }) =>
    tw({
      "text-secondary": !hasError && !isDisabled,
      "text-interactive-error": hasError,
      "text-interactive-disabled-text": isDisabled,
    }),
  errorIcon: tw("text-interactive-error", "h-3.5", "w-3.5", "mr-2"),
};

const DropdownViewBase = ({
  align = "left",
  ariaLabel,
  errorMessage,
  fluid = false,
  hasError = false,
  helperText,
  iconName,
  isBorderless = false,
  isDisabled = false,
  label,
  labelPosition = "top",
  onSelectOption,
  options,
  selectedOption,
}: Props) => (
  <Listbox
    as="div"
    className={styles.base({ fluid })}
    disabled={isDisabled}
    onChange={onSelectOption}
    value={selectedOption}
  >
    <Listbox.Button
      aria-label={ariaLabel}
      className={styles.selectedOptionButton({
        labelPosition,
        fluid,
        isDisabled,
      })}
    >
      {({ open }) => (
        <>
          {label && (
            <Listbox.Label
              className={styles.label({ labelPosition, isDisabled })}
            >
              <Text as={isBorderless ? "body1" : "body2"}>{label}</Text>
            </Listbox.Label>
          )}
          <div
            className={styles.selectedOptionContainer({
              isBorderless,
              fluid,
              hasError,
              isDisabled,
            })}
          >
            <div className={styles.selectedOptionGroup}>
              {iconName ? (
                <Icon className={tw("mr-2", "h-5", "w-5")} name={iconName} />
              ) : null}
              <Text
                as={isBorderless ? "body1" : "body2"}
                className={styles.selectedOption({ isBorderless, isDisabled })}
              >
                {selectedOption.name}
              </Text>
            </div>
            <Icon
              className={styles.arrow({ open, isDisabled })}
              name="dropdown-arrow"
            />
            <Transition
              className={styles.optionsContainer(align, fluid)}
              enter={styles.optionsTransition.enter}
              enterFrom={styles.optionsTransition.enterFrom}
              enterTo={styles.optionsTransition.enterTo}
              leave={styles.optionsTransition.leave}
              leaveFrom={styles.optionsTransition.leaveFrom}
              leaveTo={styles.optionsTransition.leaveTo}
            >
              <Listbox.Options className={styles.options}>
                {options.map((option) => (
                  <Listbox.Option
                    key={`listbox-option-${option.name}`}
                    value={option}
                  >
                    {({ active }) => (
                      <div className={styles.option(active)}>
                        <div className={styles.selectedOptionGroup}>
                          {option.iconName ? (
                            <Icon
                              className={tw("mr-2", "h-5", "w-5")}
                              name={option.iconName}
                            />
                          ) : null}
                          <Text
                            as={isBorderless ? "body1" : "body2"}
                            className={styles.selectedOption({
                              isBorderless,
                              isDisabled,
                            })}
                          >
                            {option.name}
                          </Text>
                        </div>
                        {selectedOption.name === option.name && (
                          <Icon className={styles.checkmark} name="checkmark" />
                        )}
                      </div>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </>
      )}
    </Listbox.Button>
    {((hasError && errorMessage) || helperText) && (
      <div className={styles.helperTextContainer}>
        {hasError && (
          <Icon className={styles.errorIcon} name="exclamation-circle" />
        )}
        <Text
          as="body2"
          className={styles.helperText({ hasError, isDisabled })}
        >
          {hasError ? errorMessage : helperText}
        </Text>
      </div>
    )}
  </Listbox>
);

export const DropdownView = memo(DropdownViewBase);
